import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { PILLARS, SERVICES, METHOD, METHOD_QUOTE, INVEST } from "../../content/site";

export const Pillars = ({ dark = false }) => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8">
    {PILLARS.map((p, i) => (
      <Reveal key={p.n} delay={i * 0.12} className={`${i === 1 ? "md:mt-12" : ""} ${i === 2 ? "md:mt-24" : ""}`} data-testid={`pillar-${p.title.toLowerCase()}`}>
        <div className="font-serif text-6xl md:text-7xl font-light text-gold leading-none">{p.n}</div>
        <h3 className={`mt-6 text-xs uppercase tracking-[0.3em] font-sans font-medium ${dark ? "text-cream" : "text-ink"}`}>{p.title}</h3>
        <div className="w-10 h-px bg-sage mt-4 mb-5" />
        <p className={`lead ${dark ? "!text-cream/70" : ""}`}>{p.text}</p>
      </Reveal>
    ))}
  </div>
);

export const ServiceCards = () => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-ink/10">
    {SERVICES.map((s, i) => (
      <Reveal key={s.title} delay={(i % 3) * 0.1} className="bg-cream" data-testid={`service-card-${i}`}>
        <div className="group h-full p-8 md:p-10 lg:p-12 bg-white hover:bg-cream transition-colors duration-500 flex flex-col min-h-[300px]">
          <div className="flex items-start justify-between">
            <s.icon size={30} strokeWidth={1} className="text-sage group-hover:text-gold transition-colors duration-500" />
            <span className="font-serif text-2xl text-ink/20 font-light">0{i + 1}</span>
          </div>
          <h3 className="mt-auto pt-14 font-sans text-xs uppercase tracking-[0.25em] font-medium text-ink">{s.title}</h3>
          <div className="w-8 h-px bg-sage my-4 group-hover:w-16 group-hover:bg-gold transition-all duration-500" />
          <p className="text-sm md:text-base font-light leading-relaxed text-ink/70">{s.text}</p>
        </div>
      </Reveal>
    ))}
  </div>
);

export const MethodTimeline = () => (
  <div className="relative">
    <div className="hidden lg:block absolute top-[38px] left-0 right-0 h-px bg-ink/10">
      <Reveal className="h-full bg-gold origin-left" initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} transition={{ duration: 2.2, ease: "easeInOut" }} />
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-12 lg:gap-6">
      {METHOD.map((m, i) => (
        <Reveal key={m.n} delay={i * 0.12} data-testid={`method-step-${m.n}`}>
          <div className="flex items-center gap-4 lg:block">
            <div className="relative z-10 w-[76px] h-[76px] rounded-full border border-sage/50 bg-cream flex items-center justify-center">
              <m.icon size={24} strokeWidth={1} className="text-sage" />
            </div>
          </div>
          <div className="mt-6 font-serif text-4xl font-light text-gold leading-none">{m.n}</div>
          <h3 className="mt-3 font-sans text-xs uppercase tracking-[0.25em] font-medium text-ink">{m.title}</h3>
          <p className="mt-3 text-sm font-light leading-relaxed text-ink/70">{m.text}</p>
        </Reveal>
      ))}
    </div>
    <Reveal className="mt-20 md:mt-28 text-center">
      <p className="font-serif italic font-light text-3xl md:text-4xl lg:text-5xl tracking-tight text-ink max-w-4xl mx-auto" data-testid="method-quote">“{METHOD_QUOTE}”</p>
    </Reveal>
  </div>
);

export const ProcessFlow = ({ dark = true }) => (
  <div data-testid="invest-process-flow" className="flex flex-wrap items-center justify-center lg:justify-between gap-y-8 gap-x-3">
    {INVEST.flow.map((step, i) => (
      <Reveal key={step} delay={i * 0.1} className="flex items-center gap-3">
        <div className="text-center">
          <div className={`font-serif text-2xl md:text-3xl font-light tracking-tight ${dark ? "text-cream" : "text-ink"}`}>{step}</div>
          <div className="mx-auto mt-2 w-6 h-px bg-sage-light" />
        </div>
        {i < INVEST.flow.length - 1 && <ArrowRight size={18} strokeWidth={1} className={`hidden sm:block ${dark ? "text-cream/40" : "text-ink/40"}`} />}
      </Reveal>
    ))}
  </div>
);

export const CTABand = ({ eyebrow, title, text, primary, secondary, dark = false }) => (
  <section className={`section ${dark ? "bg-ink text-cream" : "bg-beige text-ink"}`}>
    <div className="container-hh grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
      <Reveal className="lg:col-span-8">
        {eyebrow && <div className="eyebrow mb-5">{eyebrow}</div>}
        <h2 className="h-section">{title}</h2>
        {text && <p className={`lead mt-6 max-w-2xl ${dark ? "!text-cream/70" : ""}`}>{text}</p>}
      </Reveal>
      <Reveal delay={0.15} className="lg:col-span-4 flex flex-wrap gap-4 lg:justify-end">
        {primary && <Link to={primary.to} data-testid={primary.testId} className={dark ? "btn-light" : "btn-primary"}>{primary.label}</Link>}
        {secondary && <Link to={secondary.to} data-testid={secondary.testId} className={dark ? "btn-outline-light" : "btn-outline"}>{secondary.label}</Link>}
      </Reveal>
    </div>
  </section>
);
